import { usePutEnergyInitiativeMutation } from '@/api/recalcV2/endpoints/energyInitiativesApiSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { formatNumber } from '@/utils/formatters';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import { alpha, Avatar, Box, Chip, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { daDK } from '@mui/x-data-grid/locales';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import InitiativeActionCell from './InitiativeActionsCell';

import StateChip from './helpers';

import { useMemo } from 'react';
import InitiativeTimestampSummaryIndicator from './InitiativeTimestampSummaryIndicator';

const getTrendColor = (percentage) => {
  if (percentage >= 0) {
    return 'success'; // Green for positive savings
  } else {
    return 'error'; // Red for negative savings
  }
};

const toApiTS = (input) => {
  if (!input) return null;
  let dt;
  if (input instanceof Date) dt = DateTime.fromJSDate(input);
  else if (typeof input === 'number') dt = DateTime.fromMillis(input);
  else dt = DateTime.fromISO(String(input), { setZone: true });
  if (!dt.isValid) return null;
  return dt.toUTC().toISO();
};

const deriveState = (ini) => {
  const { baselineStart, baselineEnd, comparisonStart, comparisonEnd } = ini;
  if (baselineStart && baselineEnd && comparisonStart && comparisonEnd)
    return 'Done';
  if (baselineStart && baselineEnd && comparisonStart) return 'Active';
  if (baselineStart || baselineEnd) return 'Initialized';
  if (baselineStart === null) return 'Created';
  return 'Created';
};

const uiDateChangesToApiPatch = (changes = {}) => {
  const patch = {};
  const has = (k) => Object.prototype.hasOwnProperty.call(changes, k);
  if (has('baselineStart'))
    patch.baselineStart = changes.baselineStart
      ? toApiTS(changes.baselineStart)
      : null;
  if (has('baselineEnd'))
    patch.baselineEnd = changes.baselineEnd
      ? toApiTS(changes.baselineEnd)
      : null;
  if (has('comparisonStart'))
    patch.comparisonStart = changes.comparisonStart
      ? toApiTS(changes.comparisonStart)
      : null;
  if (has('comparisonEnd'))
    patch.comparisonEnd = changes.comparisonEnd
      ? toApiTS(changes.comparisonEnd)
      : null;
  return patch;
};

const buildPutBody = (prevRaw = {}, uiChanges = {}) => {
  const datePatch = uiDateChangesToApiPatch(uiChanges);
  const next = { ...prevRaw, ...datePatch };
  if (Object.prototype.hasOwnProperty.call(datePatch, 'comparisonEnd')) {
    next.completed = datePatch.comparisonEnd ?? prevRaw.completed ?? null;
  }
  const norm = (v) => (v ? toApiTS(v) : null);
  const newState = deriveState(next);
  return {
    ...prevRaw,
    name: next.name ?? '',
    description: next.description ?? '',
    state: newState,
    completed: norm(next.completed),
    baselineStart: norm(next.baselineStart),
    baselineEnd: norm(next.baselineEnd),
    comparisonStart: norm(next.comparisonStart),
    comparisonEnd: norm(next.comparisonEnd),
    preferredModel:
      typeof next.preferredModel === 'number' ? next.preferredModel : 0,
    energyTreeNodes: Array.isArray(next.energyTreeNodes)
      ? next.energyTreeNodes
      : [],
  };
};

const formatDateRange = (startISO, endISO) => {
  if (!startISO && !endISO) return '—';

  const formatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (startISO && !endISO) return `${formatter.format(new Date(startISO))} –`;
  if (!startISO && endISO) return `– ${formatter.format(new Date(endISO))}`;

  const start = new Date(startISO);
  const end = new Date(endISO);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const sameMonth =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth();

  const sameYear = start.getFullYear() === end.getFullYear();

  const dayMonthFormatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
  });

  const fullFormatter = formatter;

  if (sameDay) return fullFormatter.format(start);
  if (sameMonth) return `${start.getDate()}.–${fullFormatter.format(end)}`;
  if (sameYear)
    return `${dayMonthFormatter.format(start)} – ${fullFormatter.format(end)}`;
  return `${fullFormatter.format(start)} – ${fullFormatter.format(end)}`;
};

const InitiativeDataGrid = ({
  initiatives,
  energyGroup,
  handleViewInitiative,
  handleEditInitiative,
  handleDeleteInitiative,
  isLoading,
  initiativeStateFilter,
}) => {
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const dispatch = useDispatch();
  const [putEnergyInitiative, putStatus] = usePutEnergyInitiativeMutation();
  const [savingIds, setSavingIds] = React.useState(() => new Set());
  const [overrides, setOverrides] = React.useState({});
  const apiLoading = isLoading || putStatus.isLoading;

  const columnVisibilityModel = useMemo(
    () => ({
      co2eSavings: initiativeStateFilter === 'Archived',
      estimatedSavingsToDate: initiativeStateFilter === 'Archived',
      estimatedCo2eSavingsToDate: initiativeStateFilter === 'Archived',
      estimatedSavingsYearly: initiativeStateFilter === 'Archived',
      actualEnergyUsage: initiativeStateFilter !== 'Archived',
      energySavings: initiativeStateFilter === 'Archived',
      id: false,
    }),
    [initiativeStateFilter],
  );

  // Sort by the relevant percentage column for the current mode

  const columns = [
    {
      field: 'id',
      headerName: t('columns.id.header'),
      hidden: true,
    },
    {
      field: 'name',
      headerName: t('columns.name.header'),
      description: t('columns.name.description'),
      flex: 2,
      sortable: false, // disables sorting for this column
      filterable: false,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: params.row.advancedSavingsAvailable
                  ? alpha('#c2dd00', 0.1)
                  : 'transparent',
                mr: 1,
                borderRadius: 3,
              }}
            >
              <ModelTrainingRoundedIcon
                sx={(theme) => ({
                  color: params.row.advancedSavingsAvailable
                    ? '#96b100'
                    : theme.palette.mode === 'light'
                      ? 'lightgray'
                      : 'gray',
                })}
              />
            </Avatar>
            <Typography>{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'baselinePeriod',
      headerName: t('columns.duration.header'),
      description: t('columns.duration.description'),
      flex: 3,
      sortable: false, // disables sorting for this column
      filterable: false,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          justifyContent={'center'}
        >
          <Box display="flex" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('common.baseline_period')}:{' '}
            </Typography>
            <Typography variant="caption">
              {formatDateRange(
                params.row.baselineStart,
                params.row.baselineEnd,
              )}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Typography variant="caption" color="textSecondary">
              {t('common.comparison_period')}:{' '}
            </Typography>
            <Typography variant="caption">
              {formatDateRange(
                params.row.comparisonStart,
                params.row.comparisonEnd,
              )}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'estimatedSavingsToDate',
      headerName: t('columns.estimated_savings_to_date.header'),
      description: t('columns.estimated_savings_to_date.description'),
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params?.value;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography
                  minWidth={130}
                >{`${formattedValue} kWh`}</Typography>
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[
                          getTrendColor(params.row.energySavingsPercentage)
                        ].main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[
                            getTrendColor(params.row.energySavingsPercentage)
                          ].dark
                        : theme.palette[
                            getTrendColor(params.row.energySavingsPercentage)
                          ].light,
                  }}
                  label={
                    params.row.energySavingsPercentage
                      ? `${params.row.energySavingsPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />{' '}
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
    {
      field: 'estimatedCo2eSavingsToDate',
      headerName: t('columns.estimated_co2_savings_to_date.header'),
      description: t('columns.estimated_co2_savings_to_date.description'),
      flex: 2,
      minWidth: 180,
      renderCell: (params) => {
        const value = params?.value / 1000;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography
                  minWidth={130}
                >{`${formattedValue} kg CO2e`}</Typography>
                <Chip
                  variant="filled"
                  sx={{
                    marginLeft: 1,
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      alpha(
                        theme.palette[
                          getTrendColor(params.row.co2eSavingsPercentage)
                        ].main,
                        0.1,
                      ),
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? theme.palette[
                            getTrendColor(params.row.co2eSavingsPercentage)
                          ].dark
                        : theme.palette[
                            getTrendColor(params.row.co2eSavingsPercentage)
                          ].light,
                  }}
                  label={
                    params.row.co2eSavingsPercentage
                      ? `${params.row.co2eSavingsPercentage.toFixed(1)}%`
                      : '0%'
                  }
                  size="small"
                />{' '}
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
    {
      field: 'actualEnergyUsage',
      headerName: t('columns.actual_energy_usage.header'),
      description: t('columns.actual_energy_usage.description'),
      flex: 2,
      renderCell: (params) => {
        const value = params?.value;
        const formattedValue = value ? formatNumber(value) : '-';
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            {value ? (
              <>
                <Typography
                  minWidth={130}
                >{`${formattedValue} kWh`}</Typography>
              </>
            ) : (
              '-'
            )}
          </Box>
        );
      },
    },
    {
      field: 'state',
      headerName: t('columns.state.header'),
      description: t('columns.state.description'),
      flex: 1,
      sortable: false, // disables sorting for this column
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <StateChip state={row.state} />
          </Box>
        );
      },
    },
    {
      field: 'timestampSummary',
      headerName: '',
      description: 'Summary of timestamps available for the initiative',
      renderCell: (params) => {
        const summary = params?.value;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <InitiativeTimestampSummaryIndicator
              timestampSummary={summary}
              initiative={params.row}
            />
          </Box>
        );
      },
    },
    // {
    //   field: 'test',
    //   headerName: 'Handling',
    //   flex: 1,
    //   minWidth: 120,
    //   renderCell: (params) => {
    //     const row = params.row;
    //     const showStartBaseline = !row.baselineStart;
    //     const showEndBaseline = !!row.baselineStart && !row.baselineEnd;
    //     const showImplementationDone =
    //       !!row.baselineEnd && !row.comparisonStart;
    //     const showComplete = !!row.comparisonStart && !row.comparisonEnd;
    //     const disabled = apiLoading || savingIds.has(row.id);

    //     return (
    //       <Box display="flex" alignItems="center" width="100%" height="100%">
    //         <Stack
    //           direction="row"
    //           spacing={0.75}
    //           sx={{ ml: 1, flexWrap: 'wrap' }}
    //         >
    //           {showStartBaseline && (
    //             <Button
    //               size="small"
    //               variant="contained"
    //               onClick={() => startBaseline(row.id)}
    //               disabled={disabled}
    //             >
    //               {t('common.start_baseline', 'Start baseline')}
    //             </Button>
    //           )}
    //           {showEndBaseline && (
    //             <Button
    //               size="small"
    //               variant="contained"
    //               onClick={() => endBaseline(row.id)}
    //               disabled={disabled}
    //             >
    //               {t('common.end_baseline', 'Afslut baseline')}
    //             </Button>
    //           )}
    //           {showImplementationDone && (
    //             <Button
    //               size="small"
    //               variant="outlined"
    //               onClick={() => implementationDone(row.id)}
    //               disabled={disabled}
    //             >
    //               {t('common.implementation_done', 'Implementering færdig')}
    //             </Button>
    //           )}
    //           {showComplete && (
    //             <Button
    //               size="small"
    //               variant="outlined"
    //               color="success"
    //               onClick={() => complete(row.id)}
    //               disabled={disabled}
    //             >
    //               {t('common.complete', 'Færdig')}
    //             </Button>
    //           )}
    //         </Stack>
    //       </Box>
    //     );
    //   },
    // },
    {
      field: 'actions',
      headerName: '',
      description: t('columns.actions.description'),
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <InitiativeActionCell
            row={params.row}
            onView={(row) => {
              handleViewInitiative(row);
            }}
            onEdit={(row) => handleEditInitiative(row)}
            onDelete={(row) => handleDeleteInitiative(row)}
            onResetDates={(row) => resetDates(row.id)}
          />
        </Box>
      ),
    },
  ];

  const baseRows = initiatives.map((initiative) => ({
    id: Number(initiative.energyInitiativeId),
    name: initiative.name,
    description: initiative.description,
    baselineStart: initiative.baselineStart,
    baselineEnd: initiative.baselineEnd,
    comparisonStart: initiative.comparisonStart,
    comparisonEnd: initiative.comparisonEnd,
    energyTreeNodes: initiative.energyTreeNodes,
    state: initiative.state,
    createdDate: initiative.createdDate,
    changedDate: initiative.changedDate,
    completedDate: initiative.completedDate,
    data: initiative.energySavings,
    timestampSummary: initiative.energySavings?.TimestampsSummary,
    actualCO2eEmission:
      initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.ActualCO2eEmission,
    actualEnergyUsage: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.ActualEnergyUsage
      : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.ActualEnergyUsage,
    co2eSavings: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavings
      : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavings,
    co2eSavingsPercentage: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage
      : initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
          ?.CO2eSavingsPercentage,
    averageBaselineEnergyUsage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageBaselineEnergyUsage,
    averageComparisonEnergyUsage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageComparisonEnergyUsage,
    energySavings: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavings
      : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavings,
    energySavingsPercentage: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.AdvancedEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage
      : initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
          ?.EnergySavingsPercentage,
    advancedSavingsAvailable: initiative.energySavings?.AdvancedModelUsed,
    advancedEnergySavings: initiative.energySavings?.AdvancedEnergySavings,
    estimatedSavingsToDate: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings?.AdvEstimatedTotalSavings
      : initiative.energySavings?.ForecastedSavings?.BasicEstimatedTotalSavings,
    estimatedSavingsYearly: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings?.AdvEstimatedYearlySavings
      : initiative.energySavings?.ForecastedSavings
          ?.BasicEstimatedYearlySavings,
    estimatedCo2eSavingsToDate: initiative.energySavings?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings
          ?.AdvEstimatedTotalSavingsCO2e
      : initiative.energySavings?.ForecastedSavings
          ?.BasicEstimatedTotalSavingsCO2e,
    estimatedCo2eSavingsPercentageToDate: initiative.energySavings
      ?.AdvancedModelUsed
      ? initiative.energySavings?.ForecastedSavings?.AdvPercentageSavingsCO2e
      : initiative.energySavings?.ForecastedSavings
          ?.BasicEstimatedTotalSavingsCO2e,
    main: initiative,
  }));

  const rows = React.useMemo(
    () => baseRows.map((r) => ({ ...r, ...(overrides[r.id] || {}) })),
    [baseRows, overrides],
  );

  const filteredRows = useMemo(() => {
    if (!initiativeStateFilter) return rows;
    const filter = initiativeStateFilter.toLowerCase();
    if (filter === 'archived') {
      return rows.filter((r) => r.state === 'Archived');
    }
    if (filter === 'done') {
      return rows.filter((r) => ['Done', 'Active'].includes(r.state));
    }
    if (filter === 'created') {
      return rows.filter((r) => ['Created', 'Initialized'].includes(r.state));
    }
    return rows;
  }, [rows, initiativeStateFilter]);

  const nowISO = () => toApiTS(new Date());

  const getAccountId = () =>
    energyGroup?.AccountId ??
    energyGroup?.accountId ??
    energyGroup?.id ??
    energyGroup?.EnergyGroupId ??
    energyGroup?.energyGroupId;

  const persistDates = async (id, changes) => {
    const prevRow = baseRows.find((r) => r.id === id);
    if (!prevRow) {
      dispatch(
        showSnackbar({ severity: 'error', message: 'Initiativ ikke fundet.' }),
      );
      return;
    }
    const accountId = getAccountId();
    if (!accountId) {
      dispatch(
        showSnackbar({
          severity: 'error',
          message: 'Mangler accountId til opdatering.',
        }),
      );
      return;
    }

    // optimistic overlay
    setSavingIds((s) => new Set(s).add(id));
    setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), ...changes } }));

    // build full PUT body (merge server body + date patch)
    const baseMain = overrides[id]?.main || prevRow.main;
    const fullBody = buildPutBody(baseMain, changes);
    const nextState = deriveState(fullBody);

    try {
      await putEnergyInitiative({
        accountId,
        energyInitiativeId: prevRow.id,
        updates: { ...fullBody, state: nextState },
      }).unwrap();

      // keep our local server body in sync for subsequent clicks before a refetch
      setOverrides((o) => ({
        ...o,
        [id]: {
          ...(o[id] || {}),
          ...changes,
          main: fullBody,
          state: nextState,
        },
      }));

      dispatch(showSnackbar({ severity: 'success', message: 'Datoer gemt.' }));
    } catch (err) {
      // rollback overlay changes
      setOverrides((o) => {
        const { [id]: _, ...rest } = o;
        return rest;
      });
      dispatch(
        showSnackbar({
          severity: 'error',
          message:
            err?.data?.message || err?.message || 'Kunne ikke gemme datoer.',
        }),
      );
    } finally {
      setSavingIds((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    }
  };

  const startBaseline = (id) => persistDates(id, { baselineStart: nowISO() });
  const endBaseline = (id) => persistDates(id, { baselineEnd: nowISO() });
  const implementationDone = (id) =>
    persistDates(id, { comparisonStart: nowISO() });
  const complete = (id) => persistDates(id, { comparisonEnd: nowISO() });
  const resetDates = (id) =>
    persistDates(id, {
      baselineStart: null,
      baselineEnd: null,
      comparisonStart: null,
      comparisonEnd: null,
    });

  return (
    <DataGridPremium
      localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
      disableColumnMenu // hides the 3-dot menu in headers
      disableColumnFilter // disables filtering UI
      disableColumnSelector // disables "Show/Hide columns" menu
      disableColumnResize // prevents resizing
      disableRowSelectionOnClick // no row selection
      disableColumnSorting // disables sorting
      disableColumnReorder //
      rows={filteredRows}
      columns={columns}
      columnHeaderHeight={36}
      hideFooter
      pagination
      density="comfortable"
      disableSelectionOnClick
      pageSizeOptions={[10, 20, 50]}
      sortModel={[{ field: 'id', sort: 'desc' }]}
      columnVisibilityModel={columnVisibilityModel}
      loading={isLoading}
      slotProps={{
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
          borderTop: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiTablePagination-root': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
          borderTop: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiDatagrid-root': {
          border: 0,
        },
        '.MuiDataGrid-withBorderColor': {
          border: 'none !important',
        },
        '& .MuiDatagrid-footerContainer': {
          border: 0,
        },
        '& .MuiDatagrid-bottomContainer': {
          border: 0,
        },
        '& .MuiDataGrid-main': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
        },
        '& .MuiDataGrid-columnsContainer': {
          backgroundColor: 'blue',
        },
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '& .selected-row': {
          backgroundColor: (theme) => theme.palette.action.selected,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.selected,
          },
        },
        '& .MuiDataGrid-scrollbar': {
          overflow: 'hidden !important',
        },
      }}
    />
  );
};

export default InitiativeDataGrid;
